import type { WordCloudT } from '@mentimeter/question-modules-types';
import { useCallback } from 'react';
import type { WordCloudStaticContent } from '@core-api/presentation/types/response';
import { useFlag } from '@mentimeter/flags/confidence/client';
import { usePresentationContext } from '../../legacy-context/presentationContext';
import { getVisualizationState } from '../../utils/getVisualizationState';
import { EditorEmptyState } from './presentation/EditorEmptyState';
import { WordCloudPresentation } from './WordCloudPresentation';

export function WordCloudPresentationDataHandler() {
  const {
    InteractiveCanvasComponents,
    useQuestion,
    useConfig,
    useResults,
    useModeration,
    useTestVotes,
    usePace,
  } = usePresentationContext();
  const slide = useQuestion();

  const { readonly, enableAnimations, mode } = useConfig();
  const { testVotesShowing } = useTestVotes();
  const { currentStep } = usePace();

  const { results = [], respondents } = useResults<WordCloudT>();

  const { value: isNewLookFlagEnabled } = useFlag(
    'sli-new-look-word-cloud.enabled',
    false,
  );

  // Moderation
  const { showUi, toggleWord } = useModeration();
  const handleRemoveWord = useCallback(
    (key: string) => (showUi ? toggleWord(key) : undefined),
    [showUi, toggleWord],
  );

  const visualizationState = getVisualizationState({
    mode,
    currentStep,
  });

  const staticContent = slide.staticContent as WordCloudStaticContent;

  if (mode === 'idle' && respondents === 0) {
    return (
      <EditorEmptyState
        title={slide.title}
        styledTitle={staticContent.styledTitle as Record<string, unknown>}
        label={staticContent.label}
        longerDescription={slide.interactiveContents[0]!.description}
        fontSizeOffset={staticContent.textSizeOffset}
        CanvasVisualizationArea={
          InteractiveCanvasComponents?.CanvasVisualizationArea
        }
        isNewLookFlagEnabled={isNewLookFlagEnabled}
      />
    );
  }

  return (
    <WordCloudPresentation
      title={slide.title}
      styledTitle={staticContent.styledTitle as Record<string, unknown>}
      label={staticContent.label}
      longerDescription={slide.interactiveContents[0]!.description}
      fontSizeOffset={staticContent.textSizeOffset}
      shouldHideResults={
        slide.responseVisibility === 'hidden' ||
        visualizationState === 'hide-answers'
      }
      onRemoveWord={handleRemoveWord}
      isModerationEnabled={!readonly && showUi}
      results={results}
      shouldAnimate={enableAnimations}
      CanvasVisualizationArea={
        InteractiveCanvasComponents?.CanvasVisualizationArea
      }
      isNewLookFlagEnabled={isNewLookFlagEnabled}
      isTestVotesShowing={testVotesShowing}
    />
  );
}
